import { intensiveLocateMethodKind } from '@sb/ai/intensiveLocateMethods';
import { isUnboundedMaxMatchAreaCm2 } from '@sb/ai/matchArea';
import type { Step as RcStep } from '@sb/remote-control/types';
import { intensiveAIFeaturesAllowed } from '@sb/service-interfaces/host/processorGate';

import {
  type ValidationRule,
  type RoutineView,
  type ValidationContext,
} from '../../types';
import {
  checkCameraCalibration,
  noCameraSelectedError,
  resolveCameraEquipmentReference,
} from '../../util/cameraEquipment';
import { ActionRequiredError, type WarningFinding } from '../../util/errors';
import { validateCodeExpression } from '../../util/validators';

import { CanonicalStepRuleBase } from './base';
import type { CanonicalValidateArgs } from './base';
import {
  checkCaptureDestination,
  checkCaptureFileName,
  checkCaptureFilepath,
} from './captureConfigChecks';

class LocateRule extends CanonicalStepRuleBase {
  public constructor() {
    super('steps.Locate', 'Locate');
  }

  protected validate({
    args,
    routine,
    ctx,
    warn,
  }: CanonicalValidateArgs): void {
    type LocArgs = Extract<RcStep.Arguments, { argumentKind: 'Locate' }>;
    const a = args as LocArgs;

    // Before the configuration checks: no amount of configuring makes a
    // learned detector runnable on this box, so reporting a missing workplane
    // first would send the operator on a pointless errand.
    this.checkProcessor(a.method?.kind, ctx);

    const isManualPlane =
      (a.projectionMethod ?? 'ManualPlane') === 'ManualPlane';

    if (isManualPlane) {
      this.checkPlaneSelected(a);
      this.checkPlaneValid(a, routine, ctx);
    }

    this.checkPositionListName(a);
    this.checkCameraEquipment(a, ctx);
    warn(this.checkCaptureConfig(a, routine, ctx));
    this.checkMethod(a, ctx);
  }

  /**
   * Validate camera equipment: it must exist, be a camera, and have intrinsics
   * + extrinsics. Shares the resolver and calibration check with the Locate
   * skill step.
   *
   * Gated on `useCppCameraBot`: only that path reads `cameraEquipmentId`. With
   * the flag off the runtime ignores it and uses the legacy wrist camera, so a
   * stale or disabled leftover id must not block the routine.
   *
   * @throws {ActionRequiredError}
   */
  private checkCameraEquipment(
    args: Extract<RcStep.Arguments, { argumentKind: 'Locate' }>,
    ctx: ValidationContext,
  ): void {
    if (!ctx.featureFlags?.useCppCameraBot) return;

    if (!args.cameraEquipmentId) {
      throw noCameraSelectedError('cameraEquipmentId');
    }

    const camera = resolveCameraEquipmentReference(
      ctx,
      args.cameraEquipmentId,
      {
        subject: 'this step',
        openTarget: 'step',
        fieldId: 'cameraEquipmentId',
      },
    );

    // Undefined only when the caller loaded no equipment — nothing to check.
    if (!camera) return;

    checkCameraCalibration(camera, {
      subject: 'this step',
      fieldId: 'cameraEquipmentId',
    });
  }

  /**
   * Validate the `method` field. Each `method.kind` has its own checks; this
   * dispatches to per-kind validators.
   *
   * @throws {ActionRequiredError}
   */
  private checkMethod(
    args: Extract<RcStep.Arguments, { argumentKind: 'Locate' }>,
    ctx: ValidationContext,
  ): void {
    const { method } = args;
    if (!method || typeof method !== 'object') return;

    switch (method.kind) {
      case 'BlobDetection2D':
        this.checkMethodBlobDetection2D(method);
        break;
      case 'ShapeDetection2D':
        this.checkMethodShapeDetection2D(method);
        break;
      case 'CustomCheckpointDetection2D':
        this.checkMethodCustomCheckpointDetection2D(method, ctx);
        break;
      case 'TextPromptDetection2D':
        this.checkMethodTextPromptDetection2D(method, ctx);
        break;
      default:
        // Other method kinds have no validation requirements.
        break;
    }
  }

  /**
   * The learned detectors need a control box above the P-core threshold. Same
   * gate as `RunLocateSkill`, reached through the inline step's method kind
   * rather than a persisted skill's method.
   *
   * @throws {ActionRequiredError}
   */
  private checkProcessor(
    kind: string | undefined,
    ctx: ValidationContext,
  ): void {
    // Omitted means the caller never asked the host; see ValidationContext.
    if (ctx.processorPCores == null) return;

    const intensive = intensiveLocateMethodKind(kind);
    if (!intensive) return;

    if (intensiveAIFeaturesAllowed(ctx.processorPCores)) return;

    throw new ActionRequiredError({
      kind: 'invalidConfiguration',
      message: `${intensive.label} needs more compute than this control box has, so this step cannot run here.`,
      fieldId: 'method',
    });
  }

  private checkMethodBlobDetection2D(method: {
    settings: {
      minThreshold: number;
      maxThreshold: number;
      minArea: number;
      maxArea: number;
      filterByCircularity?: boolean;
      minCircularity?: number;
      maxCircularity?: number;
    };
  }): void {
    const s = method.settings;

    if (s.maxThreshold < s.minThreshold) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Max threshold may not be smaller than min threshold',
      });
    }

    if (s.maxArea < s.minArea) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Max area may not be smaller than min area',
      });
    }

    if (
      s.filterByCircularity &&
      s.maxCircularity != null &&
      s.minCircularity != null &&
      s.maxCircularity < s.minCircularity
    ) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Max circularity may not be smaller than min circularity',
      });
    }
  }

  private checkMethodShapeDetection2D(method: {
    settings: { minScale: number; maxScale: number };
    templateImage?: { storageID?: string | null };
  }): void {
    if (method.settings.maxScale < method.settings.minScale) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Max scale may not be smaller than min scale',
      });
    }

    if (!method.templateImage?.storageID) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Template image is not set',
      });
    }
  }

  private checkMethodCustomCheckpointDetection2D(
    method: {
      settings: {
        gpuTaskRunnerId?: string | null;
        modelPath?: string | null;
        modelType?: string | null;
        modelSource?: string | null;
        roboflowWorkflowId?: string | null;
        roboflowOutputName?: string | null;
        roboflowWorkspaceName?: string | null;
      };
    },
    ctx: ValidationContext,
  ): void {
    const s = method.settings;

    if (!s.gpuTaskRunnerId) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'No GPU Task Runner selected',
        fieldId: 'method.settings.gpuTaskRunnerId',
      });
    }

    if (!this.findEquipment(ctx, s.gpuTaskRunnerId)) {
      throw new ActionRequiredError({
        kind: 'missingEquipment',
        message: `Unable to find GPU Task Runner with id ${s.gpuTaskRunnerId} in equipment manager.`,
        fieldId: 'method.settings.gpuTaskRunnerId',
      });
    }

    if (s.modelSource === 'roboflow') {
      // A Roboflow source is always a workflow — the model path it replaced is
      // retired, so a skill saved against it reads as unconfigured here rather
      // than being migrated. Neither uses a runner-hosted checkpoint
      // `modelPath`.
      if (!s.roboflowWorkflowId) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: 'No Roboflow workflow selected',
          fieldId: 'method.settings.roboflowWorkflowId',
        });
      }

      // A graph can publish several outputs and the runner cannot pick one on
      // the operator's behalf.
      if (!s.roboflowOutputName) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: 'No Roboflow workflow output selected',
          fieldId: 'method.settings.roboflowOutputName',
        });
      }

      // The run resolves the workflow against the workspace the robot's key
      // belongs to, so without it the routine would fault mid-run.
      if (!s.roboflowWorkspaceName) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: 'No Roboflow workspace recorded for the workflow',
          fieldId: 'method.settings.roboflowWorkspaceName',
        });
      }
    } else if (!s.modelPath) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'No model selected',
        fieldId: 'method.settings.modelPath',
      });
    }

    if (!s.modelType) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'No model type selected',
        fieldId: 'method.settings.modelType',
      });
    }
  }

  private checkMethodTextPromptDetection2D(
    method: {
      settings: {
        textInput?: string | null;
        gpuTaskRunnerId?: string | null;
        minMatchAreaCm2?: number | null;
        maxMatchAreaCm2?: number | null;
      };
    },
    ctx: ValidationContext,
  ): void {
    const s = method.settings;

    if (!s.textInput) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Text prompt input is empty',
        fieldId: 'method.settings.textInput',
      });
    }

    if (s.gpuTaskRunnerId && !this.findEquipment(ctx, s.gpuTaskRunnerId)) {
      throw new ActionRequiredError({
        kind: 'missingEquipment',
        message: `Unable to find GPU Task Runner with id ${s.gpuTaskRunnerId} in equipment manager.`,
        fieldId: 'method.settings.gpuTaskRunnerId',
      });
    }

    // A max at/above the ceiling means "no upper limit", so only a real max can
    // be inverted. The wizard's slider cannot produce one; the DSL and API can,
    // and an inverted range matches nothing at runtime without a word.
    if (
      s.minMatchAreaCm2 != null &&
      s.maxMatchAreaCm2 != null &&
      !isUnboundedMaxMatchAreaCm2(s.maxMatchAreaCm2) &&
      s.maxMatchAreaCm2 < s.minMatchAreaCm2
    ) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Max match area may not be smaller than min match area',
        fieldId: 'method.settings.maxMatchAreaCm2',
      });
    }
  }

  /**
   * Validate that a plane is selected.
   * Throws ActionRequiredError if not set.
   *
   * @throws {ActionRequiredError}
   */
  private checkPlaneSelected(
    args: Extract<RcStep.Arguments, { argumentKind: 'Locate' }>,
  ): void {
    if (!args.planeID) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'No workplane selected',
        fieldId: 'planeID',
      });
    }
  }

  /**
   * Validate the selected plane exists in routine or global space.
   * Throws ActionRequiredError if invalid.
   *
   * @throws {ActionRequiredError}
   */
  private checkPlaneValid(
    args: Extract<RcStep.Arguments, { argumentKind: 'Locate' }>,
    routine: RoutineView,
    ctx: ValidationContext,
  ): void {
    const allSpace = this.getSpaceItems(routine, ctx);

    if (
      !allSpace.some(
        (item) => item.id === args.planeID && item.kind === 'plane',
      )
    ) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Selected workplane is not valid',
        fieldId: 'planeID',
      });
    }
  }

  /**
   * Validate the position list name is set.
   * Throws ActionRequiredError if missing.
   *
   * @throws {ActionRequiredError}
   */
  private checkPositionListName(
    args: Extract<RcStep.Arguments, { argumentKind: 'Locate' }>,
  ): void {
    if (!args.positionListName) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Position list name is not configured',
        fieldId: 'positionListName',
      });
    }
  }

  /**
   * Validate the optional captureConfig when present.
   *
   * The destination and filename checks are shared with `RunInspectSkill`,
   * which writes through the same runner code. Locate's own part is the
   * equipment lookup below, which names only the legacy `saveEquipmentId`.
   *
   * That lookup survived the destination test being relaxed, because choosing
   * equipment that does not exist, or that cannot take a file, is still worth
   * catching before a routine runs.
   *
   * @throws {ActionRequiredError}
   */
  private checkCaptureConfig(
    args: Extract<RcStep.Arguments, { argumentKind: 'Locate' }>,
    routine: RoutineView,
    ctx: ValidationContext,
  ): WarningFinding | undefined {
    const { captureConfig } = args;

    if (!captureConfig) {
      return undefined;
    }

    checkCaptureDestination(captureConfig);
    checkCaptureFileName(captureConfig, routine, ctx);

    if (captureConfig.saveEquipmentId) {
      const storageEquipment = this.findEquipment(
        ctx,
        captureConfig.saveEquipmentId,
      );

      if (!storageEquipment) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: 'Storage equipment not found',
          fieldId: 'captureConfig.saveEquipmentId',
        });
      }

      if (
        storageEquipment.config.kind !== 'FTPServer' &&
        storageEquipment.config.kind !== 'S3Bucket'
      ) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: 'Storage equipment must be an FTP Server or S3 Bucket',
          fieldId: 'captureConfig.saveEquipmentId',
        });
      }
    }

    checkCaptureFilepath(captureConfig.saveFilepath);

    return validateCodeExpression(
      captureConfig.saveFilepath,
      'captureConfig.saveFilepath',
      'Save filepath',
    );
  }
}

export const LocateValidation: ValidationRule =
  new LocateRule().toValidationRule();
